/**
 * Portal-style API client (migrated from portal/src/app/services/api.service.ts).
 * Single entry point for dashboard HTTP calls. Paths are relative to /api.
 *
 * Authorization: Supabase session access_token when present; else
 * sessionStorage key written after OTP verify (when that flow is wired).
 */

import { getApiBaseUrl } from '@/walkin/api';
import { getSupabaseBrowserClient } from '@/lib/supabase';

const PORTAL_TOKEN_KEY = 'adoracare_portal_token';

export interface ApiEnvelope<T> {
  data: T;
  success: boolean;
  error?: string;
}

/**
 * Typed error thrown by the portal API client on any non-2xx response or
 * `success:false` body. Carries the real HTTP `status` and the backend
 * error `code` (e.g. 'NOT_FOUND') so callers can branch on those rather
 * than substring-matching the message — substring matching the message
 * (which contains the URL path) is what produced the misleading
 * "Access restricted" bug on a 404 for /api/owner/analytics.
 *
 * Extends `Error`, so existing callers that do `e instanceof Error` /
 * read `e.message` keep working unchanged.
 */
export class PortalApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(status: number, message: string, code: string | null = null) {
    super(message);
    this.name = 'PortalApiError';
    this.status = status;
    this.code = code;
    // Restore the prototype chain so `instanceof PortalApiError` works
    // after TypeScript's ES5 class-extends-builtin downleveling.
    Object.setPrototypeOf(this, PortalApiError.prototype);
  }
}

/**
 * ERROR-STATUS-PASSTHROUGH-V1: the HTTP status of a caught error, when it
 * carries one -- for forwarding into `DashboardLoadError`'s `status` prop
 * so its own 404/401/403/NO_PROFILE classification (unreachable unless the
 * caller forwards status) renders instead of its generic verbatim-message
 * fallback. Mirrors the status-forwarding idiom already proven at
 * `PatientFollowupsPage.tsx` (the one call site that does this today).
 */
export function portalErrorStatus(err: unknown): number | undefined {
  return err instanceof PortalApiError ? err.status : undefined;
}

/**
 * ERROR-STATUS-PASSTHROUGH-V1: status-safe message selection for carriers
 * that render a message string (`ErrorState.detail`, `DashboardLoadError`'s
 * generic branch, a bespoke inline banner). A `PortalApiError` 4xx status is
 * a real backend-classified domain message -- LES-009 requires it render
 * VERBATIM. Anything else (5xx, no status at all -- a raw network/protocol
 * failure such as a browser TypeError -- or a non-Error throw) is NOT safe
 * to show verbatim; the caller's OWN existing fallback text is used
 * instead, never new copy. Mirrors the classification boundary already
 * proven at `doctor-consult-view.ts`'s `consultErrorMessage()`
 * (`status >= 400 && status < 500` -> message, else a fixed floor),
 * generalized so each call site keeps its own existing fallback rather than
 * consult-specific text.
 */
export function safeErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof PortalApiError && err.status >= 400 && err.status < 500) {
    return err.message.trim() !== '' ? err.message : fallback;
  }
  return fallback;
}

function apiRoot(): string {
  const base = getApiBaseUrl().replace(/\/+$/, '');
  if (base === '') return '/api';
  return base.endsWith('/api') ? base : `${base}/api`;
}

export async function authBearer(): Promise<string | null> {
  const supabase = getSupabaseBrowserClient();
  if (supabase) {
    const { data } = await supabase.auth.getSession();
    const t = data.session?.access_token;
    if (t) return t;
  }

  if (!import.meta.env.DEV) {
    return null;
  }

  /** Injected from `.env` / `.env.local` — same string as `npm run gen:dev-dashboard-jwt`. */
  const envTok =
    typeof import.meta.env.VITE_DEV_DASHBOARD_TOKEN === 'string'
      ? import.meta.env.VITE_DEV_DASHBOARD_TOKEN.trim()
      : '';

  if (envTok.length > 0) {
    return envTok;
  }

  /** Dev only: sessionStorage portal token before Supabase/OTP flow is fully wired. */
  return sessionStorage.getItem(PORTAL_TOKEN_KEY);
}

export function setPortalOtpToken(token: string | null): void {
  if (token === null) {
    sessionStorage.removeItem(PORTAL_TOKEN_KEY);
  } else {
    sessionStorage.setItem(PORTAL_TOKEN_KEY, token);
  }
}

function extractErrorMessage(
  status: number,
  raw: unknown,
): string {
  if (raw !== null && typeof raw === 'object') {
    const o = raw as {
      success?: boolean;
      data?: { message?: string; error?: string };
      error?: string;
      message?: string;
    };
    const inner = o.data;
    if (inner && typeof inner.message === 'string' && inner.message.length > 0) {
      return inner.message;
    }
    if (inner && typeof inner.error === 'string' && inner.error.length > 0) {
      return inner.error;
    }
    if (typeof o.message === 'string' && o.message.length > 0) return o.message;
    if (typeof o.error === 'string' && o.error.length > 0) return o.error;
  }
  return `HTTP ${status}`;
}

/**
 * Pulls the backend error CODE (e.g. 'NOT_FOUND') from the response body
 * when present. The envelope shape is `{ success, data: { error, message } }`,
 * so the code lives at `data.error` (with a top-level `error` fallback).
 * A THIRD fallback (`code`) covers a second, pre-existing router shape a
 * few routes send directly (`sendJson(res, status, result.error)` where
 * `result.error` is `{ code: '...' }` -- api/router.ts's walk-in source
 * correction, appointment-conflict, and similar handlers) -- without this,
 * `PortalApiError.code` was silently `null` for every one of those
 * responses and a caller could only ever branch on HTTP `status`, never on
 * the specific outcome (e.g. `audit_failed_unconfirmed` vs `audit_failed`,
 * both 500).
 */
function extractErrorCode(raw: unknown): string | null {
  if (raw !== null && typeof raw === 'object') {
    const o = raw as { data?: { error?: string }; error?: string; code?: string };
    if (o.data && typeof o.data.error === 'string' && o.data.error.length > 0) {
      return o.data.error;
    }
    if (typeof o.error === 'string' && o.error.length > 0) return o.error;
    if (typeof o.code === 'string' && o.code.length > 0) return o.code;
  }
  return null;
}

async function parseJsonEnvelope<T>(res: Response): Promise<T> {
  const text = await res.text();
  let raw: unknown;
  try {
    raw = text.length > 0 ? JSON.parse(text) : {};
  } catch {
    throw new PortalApiError(
      res.status,
      res.ok ? 'Invalid JSON from API' : `HTTP ${res.status} ${res.statusText}`,
      null,
    );
  }

  const obj = raw as { success?: boolean; data?: T };

  if (!res.ok || obj.success === false) {
    throw new PortalApiError(
      res.status,
      extractErrorMessage(res.status, raw),
      extractErrorCode(raw),
    );
  }

  return obj.data as T;
}

export async function portalApiGet<T>(path: string, expectedBearer?: string): Promise<T> {
  const token = await authBearer();
  if(expectedBearer!==undefined && token!==expectedBearer)throw new Error('Your session changed. Reopen the saved operation in its original session.');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const url = `${apiRoot()}${path.startsWith('/') ? path : `/${path}`}`;
  const res = await fetch(url, { headers });
  return parseJsonEnvelope<T>(res);
}

/**
 * Authenticated FILE download (MONEY-DAY-CASH-TRUTH-V1 A4). A plain
 * <a href> cannot be used for any governed export: the API authenticates
 * on a Bearer header, so an anchor would arrive unauthenticated and the
 * server would refuse it. This fetches with the same auth every other call
 * uses and hands back the bytes plus the server's own filename.
 *
 * The response is NOT the JSON envelope -- an export is a file. A failure
 * still arrives as the envelope, so an error body is parsed for its
 * message rather than being written to disk as a broken download.
 */
export async function portalApiDownload(
  path: string,
): Promise<{ blob: Blob; filename: string }> {
  const token = await authBearer();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const url = `${apiRoot()}${path.startsWith('/') ? path : `/${path}`}`;
  const res = await fetch(url, { headers });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    let code: string | undefined;
    try {
      const raw = (await res.json()) as unknown;
      const obj = raw as { error?: unknown; message?: unknown };
      if (typeof obj.message === 'string') message = obj.message;
      if (typeof obj.error === 'string') code = obj.error;
    } catch {
      // A non-JSON error body: keep the status-derived message.
    }
    throw new PortalApiError(res.status, message, code ?? null);
  }
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = disposition.match(/filename="([^"]+)"/);
  return {
    blob: await res.blob(),
    filename: match?.[1] ?? 'export.csv',
  };
}

async function portalApiMutation<T>(method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: string, body: unknown, expectedBearer?: string): Promise<T> {
  const token = await authBearer();
  if(expectedBearer!==undefined && token!==expectedBearer)throw new Error('Your session changed. Reopen the saved operation in its original session.');
  const headers: Record<string, string> = {'Content-Type': 'application/json'};
  if (token) headers.Authorization = `Bearer ${token}`;
  const url = `${apiRoot()}${path.startsWith('/') ? path : `/${path}`}`;
  const res = await fetch(url, {method, headers, body: JSON.stringify(body)});
  return parseJsonEnvelope<T>(res);
}

export function portalApiPost<T>(path: string, body: unknown, expectedBearer?: string): Promise<T> {
  return portalApiMutation<T>('POST', path, body, expectedBearer);
}
export function portalApiPut<T>(path: string, body: unknown): Promise<T> {
  return portalApiMutation<T>('PUT', path, body);
}
export function portalApiPatch<T>(path: string, body: unknown, expectedBearer?: string): Promise<T> {
  return portalApiMutation<T>('PATCH', path, body, expectedBearer);
}
export function portalApiDelete<T>(path: string, body: unknown, expectedBearer?: string): Promise<T> {
  return portalApiMutation<T>('DELETE', path, body, expectedBearer);
}
